import { CollectionFieldSchema, CollectionFieldType } from '@candu-ai/collections/types';
import {
  getCollectionEmbedLocationForFieldType,
  getCollectionId,
  getCollectionIdsFromCollection,
} from '@candu-ai/collections/utils';
import { useEditor, useMultiNodes } from 'craft/hooks';
import { useComponentSchema } from 'craft/state';
import { CollectionTargetLocation } from 'craft/types';

import { useCollectionDefinitions } from './useCollectionDefinitions';

export type AvailableCollectionFieldsArgs = {
  nodeIds: string[];
  filterByCollectionFieldType?: CollectionFieldType;
};

export type AvailableCollectionFieldsHook = (
  args: AvailableCollectionFieldsArgs,
) => Record<string, CollectionFieldSchema[]>;

export const useAvailableCollectionFields: AvailableCollectionFieldsHook = ({
  nodeIds,
  filterByCollectionFieldType,
}) => {
  const { type } = useMultiNodes(nodeIds);
  const { query } = useEditor();

  // we retrieve all collection found in tree
  const collectionNodes = getCollectionId(nodeIds.length > 0 ? nodeIds[0] : undefined, query);
  const collectionIds = getCollectionIdsFromCollection(collectionNodes, query);

  const collectionContents = useCollectionDefinitions(collectionIds);
  const schema = useComponentSchema(type);

  if (!collectionContents.length || !schema) {
    return {};
  }

  const matchingSchemaFieldsByCollection = Object.values(collectionContents).reduce(
    (acc, collectionContent) => {
      const collectionEmbedLocations = collectionContent.schema.fields
        .filter(({ type: fieldType }) =>
          filterByCollectionFieldType ? fieldType === filterByCollectionFieldType : true,
        )
        .reduce((fieldsByType: Record<string, CollectionFieldSchema[]>, field) => {
          const mappedField = getCollectionEmbedLocationForFieldType(field, schema);

          Object.keys(mappedField).forEach((key: CollectionTargetLocation) => {
            if (
              mappedField[key] &&
              (fieldsByType[key] || []).findIndex(({ id }) => field.id === id) === -1
            ) {
              // eslint-disable-next-line no-param-reassign
              fieldsByType[key] = [
                ...(fieldsByType[key] || []),
                mappedField[key] as CollectionFieldSchema,
              ];
            }
          });
          return fieldsByType;
        }, {});
      // if there are no embed locations, don't add it to the object
      if (Object.keys(collectionEmbedLocations).length < 1) {
        return acc;
      }
      return {
        ...acc,
        [collectionContent.id]: collectionEmbedLocations,
      };
    },
    {} as Record<string, Record<CollectionTargetLocation, CollectionFieldSchema[]>>,
  );
  // by default, only deal with the first collection retrieved, since we do not allow collections in collections for now
  return matchingSchemaFieldsByCollection[collectionIds[0]] || {};
};
